import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Copy, Eye, ExternalLink, ShieldCheck } from "lucide-react";

const copyText = async (t) => {
  try {
    await navigator.clipboard.writeText(t || "");
    toast.success("In Zwischenablage kopiert");
  } catch (_) {
    toast.error("Kopieren fehlgeschlagen");
  }
};

const I18N = {
  de: {
    title: "Meine Lizenzen",
    subtitle: "Login-Daten für die Tools deiner Weiterbildung.",
    empty_title: "Noch keine Lizenzen freigeschaltet",
    empty_body: "Sobald dir der/die Coach:in Lizenzen zuweist, erscheinen sie hier.",
    copy_email: "E-Mail kopieren",
    copy_pw: "Passwort kopieren",
    reveal_pw: "Passwort anzeigen",
    open_login: "Bei {name} anmelden",
    secure_hint:
      "Aus Sicherheitsgründen wird das Passwort nicht angezeigt. Nutze bitte den Kopier-Button — es wird direkt in dein Login-Formular eingefügt.",
    assigned_since: "Freigeschaltet am",
    expires: "Zugang bis",
    days_left: "verbleibend",
  },
  en: {
    title: "My licenses",
    subtitle: "Login credentials for the tools of your training programme.",
    empty_title: "No licenses assigned yet",
    empty_body: "Once your coach assigns licenses to you, they will appear here.",
    copy_email: "Copy email",
    copy_pw: "Copy password",
    reveal_pw: "Reveal password",
    open_login: "Sign in to {name}",
    secure_hint:
      "For security reasons the password is not shown. Use the copy button — it will be pasted directly into the login form.",
    assigned_since: "Assigned on",
    expires: "Access until",
    days_left: "days left",
  },
};

export default function ParticipantLicenses({ lang = "de" }) {
  const t = I18N[lang] || I18N.de;
  const [rows, setRows] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/licenses/me");
      setRows(r.data.assignments || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setRows([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (rows === null) return <div className="text-xs text-slate-500 p-8">Lädt…</div>;
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-slate-300 p-10 text-center bg-white" data-testid="participant-licenses-empty">
        <KeyRound size={32} strokeWidth={1.5} className="mx-auto text-slate-400 mb-3" />
        <div className="font-medium text-slate-700">{t.empty_title}</div>
        <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto">{t.empty_body}</p>
      </div>
    );
  }

  return (
    <div data-testid="participant-licenses">
      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
          <ShieldCheck size={12} strokeWidth={1.5} className="text-[#00CCC9]" /> {t.subtitle}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((a) => (
          <LicenseCard key={a.assignment_id} assignment={a} t={t} />
        ))}
      </div>
      <div className="mt-4 text-[11px] text-slate-500 italic max-w-2xl">{t.secure_hint}</div>
    </div>
  );
}

function LicenseCard({ assignment, t }) {
  const s = assignment.slot;
  const tpl = s.template || {};
  const [revealed, setRevealed] = useState(null);

  const revealPw = async () => {
    try {
      const r = await api.post(`/licenses/me/reveal/${s.slot_id}`);
      setRevealed(r.data.password);
      setTimeout(() => setRevealed(null), 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };
  const doCopyPw = async () => {
    try {
      const r = await api.post(`/licenses/me/reveal/${s.slot_id}`);
      await copyText(r.data.password);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div
      className="border border-slate-200 bg-white flex flex-col"
      style={{ borderTop: `3px solid ${tpl.color || "#00CCC9"}` }}
      data-testid={`lic-tile-${s.slot_id}`}
    >
      <div className="p-4 flex items-start gap-3">
        <div
          className="w-12 h-12 flex items-center justify-center text-2xl shrink-0"
          style={{ background: `${tpl.color}18`, color: tpl.color }}
        >
          {tpl.icon_emoji || "🔑"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">
            {tpl.name} · Slot {s.slot_label}
          </div>
          <div className="font-mono text-sm text-slate-900 truncate mt-0.5">{s.login_email}</div>
          <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>
              {t.assigned_since}: {(assignment.assigned_at || "").slice(0, 10)}
            </span>
            {s.expires_on && (
              <>
                <span>·</span>
                <span>
                  {t.expires} {s.expires_on}
                </span>
                {s.days_left != null && s.days_left <= 30 && !s.expired && (
                  <span className="px-1.5 py-0.5 bg-[#FFD100] text-[#0A0A0A] font-bold uppercase text-[9px] tracking-overline">
                    {s.days_left} {t.days_left}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {revealed && (
        <div className="mx-4 mb-3 p-3 border border-[#FFD100] bg-[#FFFBE6]">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">
            Passwort · sichtbar 30 s
          </div>
          <div className="font-mono text-sm break-all">{revealed}</div>
        </div>
      )}

      <div className="mt-auto p-3 border-t border-slate-100 grid grid-cols-2 gap-2">
        <button
          onClick={() => copyText(s.login_email)}
          className="text-xs border border-slate-300 hover:bg-slate-100 px-3 py-2 flex items-center justify-center gap-1"
          data-testid={`lic-copy-email-${s.slot_id}`}
        >
          <Copy size={12} strokeWidth={1.5} /> {t.copy_email}
        </button>
        <button
          onClick={doCopyPw}
          className="text-xs bg-[#00CCC9] text-[#0A0A0A] hover:brightness-105 px-3 py-2 font-semibold flex items-center justify-center gap-1"
          data-testid={`lic-copy-pw-${s.slot_id}`}
        >
          <Copy size={12} strokeWidth={1.5} /> {t.copy_pw}
        </button>
        <button
          onClick={revealPw}
          className="text-xs border border-slate-300 hover:bg-slate-100 px-3 py-2 flex items-center justify-center gap-1"
          data-testid={`lic-reveal-${s.slot_id}`}
        >
          <Eye size={12} strokeWidth={1.5} /> {t.reveal_pw}
        </button>
        {tpl.login_url && (
          <a
            href={tpl.login_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-3 py-2 flex items-center justify-center gap-1"
            data-testid={`lic-open-${s.slot_id}`}
          >
            <ExternalLink size={12} strokeWidth={1.5} />{" "}
            {t.open_login.replace("{name}", tpl.name || "")}
          </a>
        )}
      </div>
    </div>
  );
}
